/**
 * Request Queue Service
 * QUEUE-001: Queues failed requests during re-auth and retries after success.
 * Prevents request loss when session expires during API calls.
 * @packageDocumentation
 */

/**
 * Represents a queued request waiting for re-authentication.
 */
interface QueuedRequest {
  url: string;
  options: RequestInit;
  reject: (reason: Error) => void;
  resolve: (value: Response) => void;
}

let isRefreshing = false;
const failedQueue: QueuedRequest[] = [];

/**
 * Checks if a re-auth session is currently in progress.
 *
 * @returns { boolean } True if currently refreshing session.
 */
export const isRefreshingSession = (): boolean => isRefreshing;

/**
 * Sets the refreshing session state.
 *
 * @param { boolean } value Whether refreshing is in progress.
 */
export const setRefreshingSession = (value: boolean): void => {
  isRefreshing = value;
};

/**
 * Adds a request to the queue while re-auth is in progress.
 *
 * @param { string } url Request URL.
 * @param { RequestInit } options Request options.
 * @returns { Promise<Response> } Promise that resolves after re-auth.
 */
export const queueRequest = (url: string, options: RequestInit): Promise<Response> => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ url, reject, options, resolve });
  });
};

/**
 * Processes the queue after successful re-authentication.
 * Retries all queued requests.
 */
export const processQueue = async (): Promise<void> => {
  const queue = [...failedQueue];
  failedQueue.length = 0;
  isRefreshing = false;

  for (const { url, reject, options, resolve } of queue) {
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
      });
      resolve(response);
    } catch (error) {
      reject(error instanceof Error ? error : new Error('Request failed'));
    }
  }
};

/**
 * Clears the queue on auth failure (e.g., user cancelled re-auth).
 *
 * @param { Error } error Error to reject with.
 */
export const rejectQueue = (error: Error): void => {
  const queue = [...failedQueue];
  failedQueue.length = 0;
  isRefreshing = false;

  for (const { reject } of queue) {
    reject(error);
  }
};
